import { serverTimestamp } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import { AuthContext } from "global/cnr/contexts/AuthContext";
import { ParentContext } from "global/cnr/contexts/ParentContext";
import { _appVersion } from "global/cnr/contexts/ServiceWorkerContext";
import { refreshPageWithCacheClear } from "global/common/actions";
import { createRefPath } from "global/firestoreData/appData/appRefPaths";
import { useContext, useEffect, useState } from "react";
import { StartContext } from "../cnr/contexts/StartContext";
import { startTypes } from "../cnr/reducers/StartReducer";

const _showCaption = true;
const _elapseTime = 120

const MotionWait = (props) => {

  const { startType: startType_props, caption, origin } = props;

  const parentContext = useContext(ParentContext)

  const authContext = useContext(AuthContext)
  const { auth_state } = authContext ?? {};
  const { authValidated, authPendingType, loggedIn, currentAuthUser } = auth_state ?? {};

  // startContext
  const startContext = useContext(StartContext);
  const { start_state } = startContext ?? {};
  const { startCaption, startDimmerOn } = start_state ?? {};

  const [elapsed, setElapsed] = useState(0); // ⏱️ Track how long spinner is showing
  const [sent, setSent] = useState(false);

  const handleGetLatest = (e) => {
    e.stopPropagation();
    refreshPageWithCacheClear()
  }

  const debugText = auth_state ? origin + '/' + authValidated + '/' + loggedIn + '/' + authPendingType + '/' + _appVersion : origin

  const variants_outside = {
    initial: { opacity: 1 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.5, delay: 1 },
  };

  const cns = {
    outside: "mdv",
    iconAndText: "cntr wait-container",
    middleIcon: "cntr start-circle-container",
    middleText: "cntr start-text-container",
    centerIcon: "start-circle",
    centerText: "start-caption",
    originText: "start-origin-text", // 👈 new style hook for the origin message
  };

  let _startCaption = startCaption;
  let _startDimmerOn = false;

  switch (startType_props) {
    case startTypes.suspense:
      _startCaption = caption ? caption : "Just a moment...";
      _startDimmerOn = true;
      break;
    default:
    // nothing
  }

  const showLoader = startDimmerOn || _startDimmerOn;

  // 🧠 Count up to 10s while loader is visible
  useEffect(() => {
    let timer;
    if (showLoader) {
      setElapsed(0); // reset timer when loader appears
      timer = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      setElapsed(0); // reset if loader disappears
    }
    return () => clearInterval(timer);
  }, [showLoader]);

  useEffect(() => {
    const sendDebugInfo = async () => {
      if (sent) return;
      setSent(true); // prevent duplicates

      const payload = {
        origin,
        ...props,
        currentAuthUser,
        // ...debugInfo,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
      };
      const { fns } = parentContext ?? {}
      const { database_fns } = fns ?? {}
      if (database_fns) {
        const refPath = createRefPath(['_errorBoundry'])
        database_fns.add_doc(refPath, payload)
      }
    };

    if (elapsed >= _elapseTime && !sent) {
      sendDebugInfo();
    }
  }, [elapsed, origin, sent]);

  return (
    <AnimatePresence>
      {showLoader && (
        <motion.div
          initial={variants_outside.initial}
          animate={variants_outside.animate}
          exit={variants_outside.exit}
          transition={variants_outside.transition}
          className={cns.outside}
        >
          <div className={cns.iconAndText}>
            <div className={cns.middleIcon}>
              <motion.span
                className={cns.centerIcon}
                animate={{ rotate: 360 }}
                transition={{ ease: "linear", duration: 2, repeat: Infinity }}
              />
            </div>

            {_showCaption && (
              <div className={cns.middleText}>
                <AnimatePresence>
                  {_startCaption && (
                    <motion.div
                      key={_startCaption}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className={cns.centerText}
                    >
                      {_startCaption}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* 👇 Show origin if 10s passed */}
                {elapsed >= _elapseTime && origin && (
                  <motion.div
                    key="origin-debug"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ duration: 0.6 }}
                    className={cns.originText}
                    style={{ color: 'white', textAlign: 'center' }}
                  >
                    <div onClick={(e) => { handleGetLatest(e); }}
                      style={{ cursor: 'pointer', border: '1px solid darkgrey', borderRadius: '4px', padding: '1em', backgroundColor: 'grey' }}
                    >
                      ⚠️ Debug: {debugText}
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MotionWait; 